/**
 * Live countdown for RATE_LIMITED (login vs email). Auth pages render this inline;
 * invite surfaces use `toastApiError` so the countdown lasts until resetAt.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatCountdown, humanizeDuration, type DurationLocale } from "@/shared/duration";
import type { RateLimitedPayload } from "@/shared/errors";

export type RateLimitNoticePayload = Pick<
  RateLimitedPayload,
  "resetAt" | "attempts" | "window_seconds" | "target"
>;

function remainingSeconds(resetAt: string): number {
  const until = Date.parse(resetAt);
  if (!Number.isFinite(until)) return 0;
  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

function durationLocale(language: string): DurationLocale {
  return language.toLowerCase().startsWith("en") ? "en" : "de";
}

export function RateLimitNotice({
  payload,
  onExpired,
}: {
  payload: RateLimitNoticePayload;
  onExpired?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [remaining, setRemaining] = useState(() => remainingSeconds(payload.resetAt));
  const onExpiredRef = useRef(onExpired);

  useEffect(() => {
    onExpiredRef.current = onExpired;
  }, [onExpired]);

  useEffect(() => {
    const tick = () => {
      const next = remainingSeconds(payload.resetAt);
      setRemaining(next);
      if (next <= 0) onExpiredRef.current?.();
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [payload.resetAt]);

  if (remaining <= 0) return null;

  const key =
    payload.target === "login" ? "errors.RATE_LIMITED_LOGIN" : "errors.RATE_LIMITED_EMAIL";
  return (
    <Alert variant="destructive" data-testid="rate-limit-notice">
      <AlertDescription>
        {t(key, {
          attempts: payload.attempts,
          window: humanizeDuration(payload.window_seconds, durationLocale(i18n.language)),
          countdown: formatCountdown(remaining),
        })}
      </AlertDescription>
    </Alert>
  );
}
